import { ClearTextSlots } from '@upright/ui/common/generic/components/typography/ClearTextSlot';
import { ComponentProps, type ReactNode, useState } from 'react';
import clsx from 'clsx';
import {
  Tooltip as AriaTooltip,
  TooltipTrigger,
  type TooltipProps as AriaTooltipProps,
  type TooltipTriggerComponentProps,
} from 'react-aria-components';

import { Focusable } from 'react-aria-components';
import { Text } from '@upright/ui/common/generic/components/typography';
import styles from './Tooltip.module.css';
import { TooltipArrow } from './TooltipArrow';

type FocusableComponentProps = ComponentProps<typeof Focusable>;

export type Props = Pick<TooltipTriggerComponentProps, 'isOpen' | 'delay' | 'closeDelay'> &
  Pick<AriaTooltipProps, 'placement' | 'triggerRef'> & {
    text?: ReactNode;
    // `white` is for a short read-only explanation with a little structure, such as two labelled
    // rows behind an ⓘ: the popover's white surface, border and inset, without its focus trap.
    theme?: 'light' | 'dark' | 'white';
    // Opt in to revealing the tooltip immediately on a pointer/tap press, in addition to the usual
    // delayed hover + focus. react-aria treats a press as "dismiss" (a tooltip is a hover/focus
    // affordance), so this is a deliberate behaviour addition, not a native flag — see the impl note
    // below. Off by default so existing tooltips are unaffected.
    openOnPress?: boolean;
  } & (
    | (Pick<FocusableComponentProps, 'children'> & {
        // Currently the only focusable targets are buttons (Button, LinkButton, IconButton etc.),
        // tabs, tags (https://github.com/adobe/react-spectrum/commit/a20933c3db86ad4e887bfd37e5d4b5ab867fbba4)
        isNonFocusableTarget: true;
      })
    | {
        isNonFocusableTarget?: false;
        children: ReactNode;
      }
  );

export function Tooltip({
  isNonFocusableTarget,
  children,
  text,
  closeDelay = 300,
  delay = 300,
  isOpen,
  openOnPress = false,
  placement,
  triggerRef,
  theme = 'dark',
}: Props) {
  // When openOnPress is set we own the open state: react-aria still drives the delayed-hover and
  // focus opening through onOpenChange (so the 500 ms warmup is preserved), while a press force-opens
  // the tooltip. react-aria's built-in trigger deliberately hides tooltips on press, so a click alone
  // would never reveal them — the wrapper below catches the pointer click that flips the state open.
  const [pressOpen, setPressOpen] = useState(false);

  const baseContent = isNonFocusableTarget ? <Focusable>{children}</Focusable> : children;

  if (!text || !children) return children;

  const triggerControl = openOnPress ? { isOpen: pressOpen, onOpenChange: setPressOpen } : { isOpen };

  const content = openOnPress ? (
    // display: contents wrapper adds no box or layout; it only catches a click on the real
    // (keyboard/focus-accessible) trigger inside and force-opens the tooltip. Keyboard users open it
    // via focus, so the wrapper needs no role/key handler of its own.
    // In the capture phase, because a react-aria pressable (`Button`, `IconButton`, `InfoTooltip`)
    // stops the click from bubbling: an `onClick` here never fires for those triggers, and a tap
    // on a phone, which has no hover, then opens nothing.
    <span
      style={{ display: 'contents' }}
      onClickCapture={() => {
        setPressOpen(true);
      }}
    >
      {baseContent}
    </span>
  ) : (
    baseContent
  );

  return (
    <TooltipTrigger delay={delay} closeDelay={closeDelay} {...triggerControl}>
      {content}
      <AriaTooltip
        className={clsx(styles.tooltip, styles[theme])}
        placement={placement}
        triggerRef={triggerRef}
        offset={14}
      >
        <TooltipArrow theme={theme} />
        <ClearTextSlots>
          {/* A div, not Text's default span, because `text` is often a block (rows, a Stack). The
              tooltip is a flex container, so the child was laid out as a block either way. */}
          <Text asChild>
            <div>{text}</div>
          </Text>
        </ClearTextSlots>
      </AriaTooltip>
    </TooltipTrigger>
  );
}
